import {useLingui} from '@lingui/react/macro';
import {useDispatch} from 'react-redux';
import {View, Text, Image, StyleSheet} from 'react-native';
import {useCallback, useEffect, useState, Fragment} from 'react';
import {uploadStart} from 'interface/hooks/effects/transfers';
import {IconButton} from 'interface/base/IconButton';
import storage from 'extensions/storage';
import {colors} from 'config/styles';
import {theme} from 'config/themes';
import {name} from 'config/app';
import images from 'config/images';

interface WelcomeModalProps {
  exit: () => void,
  isBusiness?: boolean;
  isMinimal?: boolean;
}

export function WelcomeModal(props: WelcomeModalProps) {
  const {t} = useLingui();
  const [statePage, setPage] = useState('welcome');
  const isPageBusiness = statePage === 'business';
  const isPageWelcome = statePage === 'welcome';
  const title = isPageWelcome
    ? t`Welcome to your new My Files!`
    : t`Explore your Business Account`;

  const dispatch = useDispatch();
  const next = useCallback(() => setPage('business'), []);
  const upload = useCallback(uploadStart(dispatch, 'myfiles'), []);

  useEffect(() => {
    try {
      storage.init(name).setItem('welcome:shown', '1');
    } catch (e) {}
  }, []);

  return (
    <View style={styles.root}>
      <Text style={styles.header}>{title}</Text>
      {isPageWelcome &&
        <View style={styles.columns}>
          <View style={styles.column}>
            <Text style={styles.subheader}>
              {t`Upload Files`}
            </Text>
            <Image style={styles.image} source={{uri: images.welcomeUpload}} resizeMode="cover"/>
            <Text style={styles.text}>
              {t`Click the upload button at the top or drag & drop files anywhere.`}
            </Text>
          </View>
          <View style={styles.column}>
            <Text style={styles.subheader}>
              {t`Browse Folders`}
            </Text>
            <Image style={styles.image} source={{uri: images.welcomeBrowse}} resizeMode="cover"/>
            <Text style={styles.text}>
              {t`Quickly browse and organize your files by using the folder browser.`}
            </Text>
          </View>
          <View style={styles.column}>
            <Text style={styles.subheader}>
              {t`View Options`}
            </Text>
            <Image style={styles.image} source={{uri: images.welcomeView}} resizeMode="cover"/>
            <Text style={styles.text}>
              {t`Toggle between the list view and the gallery view at any time.`}
            </Text>
          </View>
        </View>
      }
      {isPageBusiness &&
        <View style={styles.columns}>
          <View style={styles.column}>
            <Text style={styles.subheader}>
              {t`Advanced Sharing`}
            </Text>
            <Image style={styles.image} source={{uri: images.bizSharing}} resizeMode="cover"/>
            <Text style={styles.text}>
              {t`Select files & folders to share a link with your custom subdomain.`}
            </Text>
          </View>
          <View style={styles.column}>
            <Text style={styles.subheader}>
              {t`Customize Your Branding`}
            </Text>
            <Image style={styles.image} source={{uri: images.bizBranding}} resizeMode="cover"/>
            <Text style={styles.text}>
              {t`Customize your colors, logo, and more in your account settings.`}
            </Text>
          </View>
          <View style={styles.column}>
            <Text style={styles.subheader}>
              {t`Detailed Security Log`}
            </Text>
            <Image style={styles.image} source={{uri: images.bizSecurity}} resizeMode="cover"/>
            <Text style={styles.text}>
              {t`Know who made what changes to your account and when.`}
            </Text>
          </View>
        </View>
      }
      <View style={styles.buttons}>
        {(props.isBusiness && isPageWelcome) &&
          <Fragment>
            <IconButton
              mode="primary"
              text={t`Next`.toUpperCase()}
              title={t`Show next page`}
              onPress={next}
            />
            <View style={styles.spacer}/>
          </Fragment>
        }
        {(!props.isBusiness || isPageBusiness) &&
          <Fragment>
            <IconButton
              mode="primary"
              text={t`Upload files`.toUpperCase()}
              title={t`Open uploader`}
              onPress={upload}
            />
            <View style={styles.spacer}/>
            <IconButton
              mode="cancel"
              text={t`Dismiss`.toUpperCase()}
              title={t`Close welcome dialog`}
              onPress={props.exit}
            />
          </Fragment>
        }
      </View>
    </View>
  );
}

export const styles = StyleSheet.create({
  root: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.dialog.border,
    backgroundColor: theme.dialog.fill,
  },
  columns: {
    flex: 1,
    flexDirection: 'row',
  },
  column: {
    flex: 1,
    width: 200,
    margin: 20,
  },
  buttons: {
    flex: 1,
    padding: 20,
    flexDirection: 'row-reverse',
  },
  spacer: {
    width: 6,
  },
  image: {
    width: 200,
    height: 120,
    marginTop: 12,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: theme.dialog.border,
  },
  header: {
    marginBottom: 16,
    marginTop: 32,
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    color: colors.primary.brand,
  },
  subheader: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.dialog.title,
  },
  text: {
    fontSize: 13,
    color: theme.dialog.text,
  },
});
